import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { readVerifierToken, clearVerifierFromUrl } from "../utils/verifierToken";
import { Container, Box, TextField, Button, Typography, Paper } from "@mui/material";
import siteLogo from "../assets/rpo_logo.png";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(() => readVerifierToken());
  useEffect(() => {
    const incoming = readVerifierToken();
    if (incoming) setToken(incoming);
    clearVerifierFromUrl();
  }, [location.key]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationLoading, setValidationLoading] = useState(Boolean(token));
  const [validationError, setValidationError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    clearVerifierFromUrl();

    if (!token) {
      setValidationLoading(false);
      setValidationError("This reset link is missing a token.");
      return () => {
        active = false;
      };
    }

    setValidationLoading(true);
    setValidationError("");

    axios
      .post(`${API_BASE}/reset-password/validate`, { token })
      .then((response) => {
        if (!active) return;
        setMaskedEmail(String(response.data?.email || "").trim());
      })
      .catch((err) => {
        if (!active) return;
        setValidationError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "This reset link is invalid or expired."
        );
      })
      .finally(() => {
        if (active) setValidationLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSuccessMessage("");

    if (!token) {
      setSubmitError("This reset link is missing a token.");
      return;
    }
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    if (!password) {
      setSubmitError("Password is required.");
      return;
    }
    if (password.length < 6) {
      setSubmitError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE}/reset-password`, {
        token,
        password,
      });

      setSuccessMessage(
        response.data?.message ||
          "Password reset successful. You can now log in with your new password."
      );
      setPassword("");
      setConfirmPassword("");

      window.setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "We couldn't reset your password right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formDisabled = validationLoading || Boolean(validationError) || Boolean(successMessage);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="md" className="login-form">
        <Paper
          elevation={3}
          sx={{ padding: 4, marginTop: 8, backgroundColor: "transparent", boxShadow: "none" }}
        >
          <Box
            component="form"
            method="post"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              border: "0",
              "& .MuiTextField-root": {
                borderRadius: "50px",
                backgroundColor: "transparent !important",
                "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
                "& .MuiInputBase-input": { color: "black" },
                "& .MuiOutlinedInput-input::placeholder": {
                  color: "black",
                  opacity: 1,
                  paddingLeft: "15px !important",
                  paddingRight: "15px !important",
                },
              },
            }}
          >
            <img src={siteLogo} className="form-logo" alt="Logo" />
            <h1 className="text-white">Choose a new password</h1>

            <div className="form-wrapper">
              {validationLoading && (
                <Typography sx={{ color: "white" }}>Checking your reset link...</Typography>
              )}

              {!validationLoading && maskedEmail && (
                <Typography sx={{ color: "white" }}>
                  Resetting password for {maskedEmail}
                </Typography>
              )}

              <TextField
                placeholder="New Password"
                variant="outlined"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={formDisabled}
              />

              <TextField
                placeholder="Confirm Password"
                variant="outlined"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                disabled={formDisabled}
              />

              <Button type="submit" variant="outlined" color="primary" disabled={formDisabled || submitting}>
                {submitting ? "Resetting..." : "Reset Password"}
              </Button>

              {validationError && <Typography color="error">{validationError}</Typography>}
              {submitError && <Typography color="error">{submitError}</Typography>}
              {successMessage && <Typography sx={{ color: "white" }}>{successMessage}</Typography>}
            </div>
          </Box>

          <Typography className="text-white" variant="body2" sx={{ marginTop: 2 }}>
            Need a new link?{" "}
            <Link className="text-white" to="/forgot-password">
              Forgot Password
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
